import { ReactRenderer } from '@tiptap/react';
import type { SuggestionOptions } from '@tiptap/suggestion';

import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';

import { FieldSuggestionList } from './FieldSuggestionList';
import type { FieldSuggestionItem } from './extensions/fieldSuggestion';
import type {
	FieldSuggestionListHandle,
	FieldSuggestionListProps,
} from './flowDocumentEditor.types';

const GAP_PX = 4;

/**
 * Above the modal, because the editor is always inside one.
 *
 * The field-binding editor opens in a fullScreen Mantine modal, which this app's theme puts at
 * `Z_INDEX.MODAL` (999) — an opaque panel over the whole viewport. At any lower value the picker
 * mounts at the caret, positions correctly, and is painted straight over: typing `{{` looked
 * like nothing happened. `GLOBAL_MENU` is the layer every other dropdown in the app uses to sit
 * above a modal, so the picker uses it too rather than inventing a number.
 */
const PANEL_Z_INDEX = Z_INDEX.GLOBAL_MENU;

type RectSource = (() => DOMRect | null) | null | undefined;

/** Keeps the panel on screen: below the caret normally, above it when there is no room. */
const place = (element: HTMLElement, getRect: RectSource): void => {
	const rect = getRect?.() ?? null;
	if (rect === null) {
		return;
	}
	const { height, width } = element.getBoundingClientRect();
	const below = rect.bottom + GAP_PX;
	const fitsBelow = below + height <= window.innerHeight;
	element.style.position = 'fixed';
	element.style.zIndex = String(PANEL_Z_INDEX);
	element.style.top = `${fitsBelow ? below : Math.max(GAP_PX, rect.top - height - GAP_PX)}px`;
	element.style.left = `${Math.min(rect.left, Math.max(GAP_PX, window.innerWidth - width - GAP_PX))}px`;
};

/**
 * Placed now and again after the browser has laid the panel out.
 *
 * `ReactRenderer` fills its element through a React state update, so at `onStart` the element is
 * still empty and measures 0×0 — `fitsBelow` then says yes however low the caret is, and the
 * panel is dropped off the bottom of the window where nothing shows it.
 */
const placeWhenPainted = (element: HTMLElement, getRect: RectSource): void => {
	place(element, getRect);
	requestAnimationFrame(() => {
		if (element.isConnected) {
			place(element, getRect);
		}
	});
};

/**
 * Mounts the picker next to the caret.
 *
 * Positioned by hand rather than with a popover library because the anchor is a caret rect the
 * suggestion plugin hands over, not an element — there is nothing for a popover to attach to.
 */
export const createFieldSuggestionRenderer =
	(): NonNullable<SuggestionOptions<FieldSuggestionItem>['render']> => () => {
		let renderer: ReactRenderer<FieldSuggestionListHandle, FieldSuggestionListProps> | null =
			null;

		const asProps = (props: {
			items: FieldSuggestionItem[];
			command: (item: FieldSuggestionItem) => void;
			query: string;
		}): FieldSuggestionListProps => ({
			items: props.items,
			command: props.command,
			query: props.query,
		});

		return {
			onStart: (props) => {
				renderer = new ReactRenderer(FieldSuggestionList, {
					props: asProps(props),
					editor: props.editor,
				});
				document.body.appendChild(renderer.element);
				placeWhenPainted(renderer.element as HTMLElement, props.clientRect);
			},
			onUpdate: (props) => {
				renderer?.updateProps(asProps(props));
				if (renderer) {
					placeWhenPainted(renderer.element as HTMLElement, props.clientRect);
				}
			},
			onKeyDown: ({ event }) => {
				if (event.key === 'Escape') {
					renderer?.destroy();
					renderer?.element.remove();
					renderer = null;
					return true;
				}
				return renderer?.ref?.onKeyDown(event) ?? false;
			},
			onExit: () => {
				renderer?.element.remove();
				renderer?.destroy();
				renderer = null;
			},
		};
	};
